import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Plane, Upload, Loader2, Map, LayoutGrid, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import AerialMapOverlay from './AerialMapOverlay';
import { pollJobsToTerminal, resolveIngestionState } from '../lib/jobPolling';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const jobCount = (j) => {
  const r = j.result || {};
  const s = r.summary || {};
  const v = s.totalCount ?? r.count ?? r.totalCount;
  return Number.isFinite(Number(v)) ? Number(v) : 0;
};

/**
 * Drone flight ingestion: upload an aerial photo set, run object counting on each
 * (grouped by a flight batchId + captureSource 'drone'), then attempt a stitched
 * site map. Always falls back to an ordered grid site map when stitching fails.
 */
const DroneFlightPanel = ({ projectId, mode, expectedQty, jobs, onRefresh }) => {
  const inputRef = useRef(null);
  const [flightId, setFlightId] = useState(null);
  const [progress, setProgress] = useState(null); // {done,total}
  const [stitching, setStitching] = useState(false);
  const [stitchedUrl, setStitchedUrl] = useState(null);
  const [aerialJob, setAerialJob] = useState(null);        // completed analysis of the stitched map
  const [aerialProgress, setAerialProgress] = useState(null); // {stage,current,total} while tiling
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'map'
  const [reportBusy, setReportBusy] = useState(false);
  const [flightStatus, setFlightStatus] = useState(null); // {state,submitted,completed,failed}

  const flightJobs = flightId ? jobs.filter((j) => j.batchId === flightId) : [];
  const total = flightJobs.reduce((s, j) => s + jobCount(j), 0);

  const uploadImageFile = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('dataType', 'object_count');
    const res = await axios.post(`${API}/projects/${projectId}/uploads`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.id;
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = '';
    if (files.length === 0) return;
    const fid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `flight-${Date.now()}`;
    setFlightId(fid);
    setStitchedUrl(null);
    setAerialJob(null);
    setAerialProgress(null);
    setViewMode('grid');
    setProgress({ done: 0, total: files.length });
    const uploadIds = [];
    const jobIds = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const uploadId = await uploadImageFile(files[i]);
        uploadIds.push(uploadId);
        const res = await axios.post(`${API}/projects/${projectId}/object-intel/run`, {
          mode,
          expectedQty: expectedQty ? parseInt(expectedQty) : null,
          uploadId,
          batchId: fid,
          captureSource: 'drone',
        });
        if (res.data?.id) jobIds.push(res.data.id);
      } catch (err) { /* continue flight; failure reflected below */ }
      setProgress({ done: i + 1, total: files.length });
    }

    // Wait for AUTHORITATIVE terminal results — never claim success from upload alone.
    setProgress({ done: 0, total: jobIds.length, phase: 'processing' });
    const { completed, failed, aiUnavailable } = await pollJobsToTerminal(API, jobIds, {
      onTick: ({ completed: c, failed: f }) => setProgress({ done: c + f, total: jobIds.length, phase: 'processing' }),
    });
    const totalFailed = failed + (files.length - jobIds.length);
    const state = resolveIngestionState({ submitted: files.length, completed, failed: totalFailed, aiUnavailable });
    setProgress(null);
    setFlightStatus({ state, submitted: files.length, completed, failed: totalFailed });
    if (state === 'AI_UNAVAILABLE') {
      toast.error('AI analysis is currently unavailable because an AI provider has not been configured.');
    } else if (state === 'FAILED') {
      toast.error(`Drone flight failed — 0 of ${files.length} photos analyzed.`);
    } else if (state === 'PARTIALLY_COMPLETED') {
      toast.warning(`Drone flight partially completed — ${completed} analyzed, ${totalFailed} failed.`);
    } else {
      toast.success(`Drone flight ingested — ${completed} of ${files.length} photos analyzed.`);
    }
    if (onRefresh) onRefresh();

    // Attempt stitch (best-effort). Grid fallback always available.
    if (uploadIds.length >= 2) {
      setStitching(true);
      try {
        const res = await axios.post(`${API}/projects/${projectId}/drone/stitch`, { uploadIds });
        if (res.data.stitched && res.data.url) {
          setStitchedUrl(res.data.url);
          setViewMode('map');
          toast.success('Stitched site map created');
          if (res.data.uploadId) analyzeStitchedMap(res.data.uploadId);
        } else {
          toast.info(res.data.reason || 'Stitching unavailable — using grid site map');
        }
      } catch (err) {
        toast.info('Stitching unavailable — using grid site map');
      } finally {
        setStitching(false);
      }
    }
  };

  // Analyze the stitched aerial map tile-by-tile, then overlay de-duplicated pins.
  const analyzeStitchedMap = async (mapUploadId) => {
    setAerialProgress({ stage: 'tiles', current: 0, total: 0 });
    try {
      const run = await axios.post(`${API}/projects/${projectId}/object-intel/run`, {
        mode, expectedQty: expectedQty ? parseInt(expectedQty) : null,
        uploadId: mapUploadId, captureSource: 'drone',
      });
      const jobId = run.data.id;
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/analysis-jobs/${jobId}`);
          if (r.data.status === 'completed') {
            clearInterval(poll);
            setAerialProgress(null);
            setAerialJob(r.data);
          } else if (r.data.status === 'failed' || tries > 60) {
            clearInterval(poll);
            setAerialProgress(null);
          } else {
            setAerialProgress(r.data.progress || { stage: 'tiles', current: 0, total: 0 });
          }
        } catch (err) { clearInterval(poll); setAerialProgress(null); }
      }, 2000);
    } catch (err) {
      setAerialProgress(null);
    }
  };

  const downloadFlightReport = async () => {
    if (!flightId) return;
    setReportBusy(true);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/object-intel/batch-report`,
        { batchId: flightId },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `drone_flight_report_${flightId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Flight report downloaded');
    } catch (e) {
      toast.error('Failed to generate flight report');
    } finally {
      setReportBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="drone-panel">
      <div className="flex items-center gap-2 mb-1">
        <Plane className="w-5 h-5 text-sky-600" />
        <h2 className="text-lg font-semibold text-slate-900">Drone Flight Ingestion</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Import an aerial photo set. We count objects per photo and build a site map — stitched when possible,
        otherwise an ordered grid. Uses the current mode ({mode}).
      </p>

      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="drone-file-input" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={!!progress || stitching}
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-sky-300 rounded-lg py-6 hover:bg-sky-50 disabled:opacity-60 mb-4"
        data-testid="drone-upload-btn"
      >
        {progress ? <Loader2 className="w-6 h-6 text-sky-600 animate-spin" /> : <Upload className="w-6 h-6 text-sky-600" />}
        <span className="font-medium">
          {progress ? `Analyzing ${progress.done}/${progress.total} photos…` : 'Upload Drone Flight Photos'}
        </span>
      </button>

      {stitching && (
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-3" data-testid="drone-stitching">
          <Loader2 className="w-4 h-4 animate-spin" /> Building stitched site map…
        </div>
      )}

      {flightStatus && (
        <div
          className={`mb-3 rounded-lg p-3 border text-sm ${
            flightStatus.state === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-800' :
            flightStatus.state === 'PARTIALLY_COMPLETED' ? 'bg-amber-50 border-amber-200 text-amber-800' :
            flightStatus.state === 'AI_UNAVAILABLE' ? 'bg-slate-50 border-slate-300 text-slate-700' :
            'bg-red-50 border-red-200 text-red-800'
          }`}
          data-testid="drone-status-banner"
        >
          <p className="font-semibold" data-testid="drone-status-state">{flightStatus.state.replace(/_/g, ' ')}</p>
          {flightStatus.state === 'AI_UNAVAILABLE' ? (
            <p className="mt-0.5">AI analysis is unavailable — no AI provider configured. Photos are uploaded and preserved.</p>
          ) : (
            <p className="mt-0.5">Completed: {flightStatus.completed} · Failed: {flightStatus.failed} · Submitted: {flightStatus.submitted}</p>
          )}
        </div>
      )}

      {flightId && flightJobs.length > 0 && (
        <div data-testid="drone-results">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold" data-testid="drone-total">
                Site total: {total} objects · {flightJobs.length} photos
              </span>
            </div>
            <div className="flex items-center gap-2">
              {stitchedUrl && (
                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                  <button onClick={() => setViewMode('map')} className={`px-2 py-1 text-xs flex items-center gap-1 ${viewMode === 'map' ? 'bg-sky-600 text-white' : 'bg-white text-slate-600'}`} data-testid="drone-view-map">
                    <Map className="w-3 h-3" /> Map
                  </button>
                  <button onClick={() => setViewMode('grid')} className={`px-2 py-1 text-xs flex items-center gap-1 ${viewMode === 'grid' ? 'bg-sky-600 text-white' : 'bg-white text-slate-600'}`} data-testid="drone-view-grid">
                    <LayoutGrid className="w-3 h-3" /> Grid
                  </button>
                </div>
              )}
              <button onClick={downloadFlightReport} disabled={reportBusy} className="px-3 py-1.5 bg-sky-600 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-sky-700 disabled:opacity-60" data-testid="drone-report-btn">
                {reportBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Flight Report
              </button>
            </div>
          </div>

          {viewMode === 'map' && stitchedUrl ? (
            <div data-testid="drone-stitched-map">
              {aerialProgress && (
                <div className="mb-2 flex items-center gap-2 text-xs text-sky-700" data-testid="aerial-tile-progress">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {aerialProgress.total
                    ? `Analyzing tile ${aerialProgress.current}/${aerialProgress.total}…`
                    : 'Preparing aerial tiles…'}
                </div>
              )}
              {aerialJob && (aerialJob.result?.detections || []).length > 0 ? (
                <AerialMapOverlay mapUrl={stitchedUrl} detections={aerialJob.result.detections} />
              ) : (
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-900">
                  <img src={stitchedUrl} alt="Stitched drone site map" className="w-full object-contain max-h-[420px]" />
                </div>
              )}
              {aerialJob && (aerialJob.result?.detections || []).length === 0 && !aerialProgress && (
                <p className="text-xs text-slate-400 mt-1" data-testid="aerial-no-pins">
                  No per-object detections to plot (aerial estimate used summary counts).
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="drone-grid-map">
              {flightJobs.map((j, idx) => (
                <div key={j.id} className="relative rounded-lg border border-slate-200 bg-slate-50 aspect-square flex flex-col items-center justify-center p-2" data-testid="drone-grid-tile">
                  <span className="absolute top-1 left-1 text-[10px] bg-slate-800 text-white rounded px-1">#{idx + 1}</span>
                  <span className={`absolute top-1 right-1 text-[10px] px-1 rounded ${j.status === 'completed' ? 'bg-green-100 text-green-700' : j.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{j.status}</span>
                  <Plane className="w-6 h-6 text-slate-300 mb-1" />
                  <span className="text-lg font-bold text-slate-800">{j.status === 'completed' ? jobCount(j) : '—'}</span>
                  <span className="text-[10px] text-slate-500">objects</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DroneFlightPanel;
